// src/App.jsx
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from 'react-hot-toast';

// --- Layout & Guards ---
import Navbar from "./components/Navbar";
import SiteFooter from "./components/SiteFooter";
import ProtectedRoute from "./auth/ProtectedRoute";
import ProfessionalRoute from "./auth/ProfessionalRoute";
import AdminRoute from "./auth/AdminRoute";

// --- Error & Loading components ---
import RouteLoading from "./components/app/RouteLoading";
import RouteError from "./components/app/RouteError";

// --- Static (named exports) ---
import { BlogListPage, BlogPostPage } from "./pages/Blog";

// --- Lazy pages (default exports) ---
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/Login"));
const Pricing = lazy(() => import("./pages/Pricing"));
const StyleLibrary = lazy(() => import("./pages/StyleLibrary"));
const HairGalleria = lazy(() => import("./pages/HairGalleria"));
const HairstyleDetail = lazy(() => import("./pages/HairstyleDetail"));
const Shop = lazy(() => import("./pages/Shop"));
const BusinessPartnerships = lazy(() => import("./pages/BusinessPartnerships")); // /partners
const Creations = lazy(() => import("./pages/Creations"));
const PartnerSignUp = lazy(() => import("./pages/PartnerSignUp"));
const PartnerOnboarding = lazy(() => import("./pages/PartnerOnboarding"));
const PartnerSubscription = lazy(() => import("./pages/PartnerSubscription"));
const PartnerProfile = lazy(() => import("./pages/PartnerProfile"));
// const FindProfessional = lazy(() => import("./pages/FindProfessional")); // Deprecated
const HairSpecialistPage = lazy(() => import("./pages/HairSpecialistPage"));
const HairSpecialistDetail = lazy(() => import("./pages/HairSpecialistDetail"));
const ProfessionalDetail = lazy(() => import("./pages/ProfessionalDetail"));
const ProListingDashboard = lazy(() => import("./pages/ProListingDashboard"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const MyCreations = lazy(() => import("./pages/MyCreations"));
const Billing = lazy(() => import("./pages/Billing"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const Account = lazy(() => import("./pages/Account"));
const SignUp = lazy(() => import("./pages/SignUp")); // General signup
const Admin = lazy(() => import("./pages/Admin")); // ✅ Main admin dashboard
const AdminLeads = lazy(() => import("./pages/AdminLeads")); // ✅ Legacy leads page
const AdminProfessionalEditor = lazy(() => import("./pages/AdminProfessionalEditor")); // ✅ New Admin Editor
const Privacy = lazy(() => import("./pages/Privacy.jsx"));
const Terms = lazy(() => import("./pages/Terms.jsx"));
const Disclaimer = lazy(() => import("./pages/Disclaimer.jsx"));
const CreationDetail = lazy(() => import("./pages/CreationDetail"));
const PublicCreation = lazy(() => import("./pages/PublicCreation"));
const HairLossCalculator = lazy(() => import("./pages/HairLossCalculator"));
const RandomHairstyleGenerator = lazy(() => import("./pages/RandomHairstyleGenerator"));
const NotFound = lazy(() => import("./pages/NotFound"));
const WhichHairstyleSuitsMeMaleFemale = lazy(() => import("./pages/WhichHairstyleSuitsMeMaleFemale"));
const HairIngredientChecker = lazy(() => import("./pages/HairIngredientChecker"));
const DevHairIngredientFixtures = lazy(() => import("./components/DevHairIngredientFixtures"));
const ChatWidget = lazy(() => import("./components/chat/ChatWidget"));

// Global layout (navbar + footer)
function MainLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
      
      {/* Chat widget (shows on all pages with layout) */}
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
    </div>
  );
}

export default function App() {
  return (
    <div className="min-h-screen w-full bg-[#0b0b12] text-white flex flex-col">
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#1f1f2e',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      />
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          {/* Public + protected routes that share the global layout */}
          <Route element={<MainLayout />} errorElement={<RouteError />}>
            {/* Home */}
            <Route index element={<Home />} />
            <Route path="/about" element={<About />} />

            {/* Public */}
            <Route path="/login/*" element={<Login />} />
            <Route path="/signup/*" element={<SignUp />} />
            <Route path="/partners/login/*" element={<Login path="/partners/login" signUpUrl="/partners/signup" afterSignInUrl="/partner/profile" />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/styles" element={<Navigate to="/hair-galleria" replace />} />
            <Route path="/hair-galleria" element={<HairGalleria />} />
            <Route path="/hair-galleria/:slug" element={<HairstyleDetail />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/partners" element={<BusinessPartnerships />} /> {/* ✅ existing page */}
            <Route path="/partners/signup/*" element={<PartnerSignUp />} />
            <Route path="/partners/onboarding" element={<PartnerOnboarding />} />
            
            {/* New SEO-Optimized Directory */}
            <Route path="/hair-specialist" element={<HairSpecialistPage />} />
            <Route path="/hair-specialist/:city" element={<HairSpecialistPage />} />
            <Route path="/hair-specialist/p/:slug" element={<HairSpecialistDetail />} />
            
            {/* Legacy professionals routes are handled by Cloudflare _redirects */}

            <Route path="/professionals/:slugOrId" element={<ProfessionalDetail />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/creations" element={<Creations />} />
            <Route path="/creations/:id" element={<CreationDetail />} />
            <Route path="/c/:shareId" element={<PublicCreation />} />
            <Route
              path="/norwood-hamilton-scale-will-i-go-bald-calculator"
              element={<HairLossCalculator />}
            />
            <Route
              path="/random-hairstyle-generator/*"
              element={<RandomHairstyleGenerator />}
            />
            <Route
              path="/which-hairstyle-suits-me-male-female/*"
              element={<WhichHairstyleSuitsMeMaleFemale />}
            />
            <Route
              path="/hair-ingredient-checker/*"
              element={<HairIngredientChecker />}
            />

            {/* Development-only test harness (no auth required) */}
            <Route
              path="/dev/hic-fixtures"
              element={<DevHairIngredientFixtures />}
            />
            
            {/* Redirects for removed features */}
            <Route path="/makeup" element={<Navigate to="/" replace />} />
            <Route path="/makeup-studio" element={<Navigate to="/" replace />} />

            {/* Protected (with global layout) */}
            <Route
              path="/creations"
              element={
                <ProtectedRoute>
                  <MyCreations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partners/subscribe"
              element={
                <ProtectedRoute>
                  <PartnerSubscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/profile"
              element={
                <ProfessionalRoute>
                  <PartnerProfile />
                </ProfessionalRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pro/listing"
              element={
                <ProtectedRoute>
                  <ProListingDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin-only */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/leads"
              element={
                <AdminRoute>
                  <AdminLeads />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/professionals/:id"
              element={
                <AdminRoute>
                  <AdminProfessionalEditor />
                </AdminRoute>
              }
            />
          </Route>

          {/* Pages WITHOUT global layout */}
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />

          {/* Studio (own layout) */}
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route element={<MainLayout />}>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
}
